// relative imports
import {
  cardExpiryStatus,
  cardExpiryThresholdDays,
} from '../../../data/app-config/appDetails';
import { getDate } from '../../shared/utilities/dateUtilities';

const formatExpiryLabel = (expiryMonth, expiryYear) =>
  `${String(expiryMonth).padStart(2, '0')}/${expiryYear}`;

/**
 *
 *  A card is valid through the end of its expiry month, so
 * comparisons use end-of-month. Non-card payment methods and
 * missing card data return an "unknown" status
 */
export const getCardExpiryStatus = (paymentSource) => {
  const card = paymentSource?.card;
  const expiryMonth = card?.expiry_month;
  const expiryYear = card?.expiry_year;

  const baseResult = {
    status: cardExpiryStatus.unknown,
    hasCard: Boolean(card),
    isExpired: false,
    isExpiringSoon: false,
    daysUntilExpiry: null,
    expiryLabel: null,
    last4: card?.last4 || null,
    brand: card?.brand || null,
  };

  // Non-card methods or incomplete expiry data cannot be evaluated.
  if (!card || !expiryMonth || !expiryYear) {
    return baseResult;
  }

  const expiryEnd = getDate(
    { year: Number(expiryYear), month: Number(expiryMonth) },
    { parseMethod: 'Object', type: 'utc', parseOptions: { zone: 'utc' } },
  ).endOf('month');

  if (!expiryEnd.isValid) {
    return baseResult;
  }

  const now = getDate(undefined, { type: 'utc' });
  const isExpired = now > expiryEnd;
  const daysUntilExpiry = expiryEnd.diff(now, 'days').days;

  return {
    ...baseResult,
    status: isExpired ? cardExpiryStatus.expired : cardExpiryStatus.valid,
    isExpired,
    daysUntilExpiry,
    expiryLabel: formatExpiryLabel(expiryMonth, expiryYear),
  };
};

export const getCardExpiryAlert = (
  paymentSource,
  { thresholdDays = cardExpiryThresholdDays } = {},
) => {
  const cardStatus = getCardExpiryStatus(paymentSource);
  const { status, isExpired, daysUntilExpiry } = cardStatus;

  const isExpiringSoon =
    status !== cardExpiryStatus.unknown &&
    !isExpired &&
    daysUntilExpiry <= thresholdDays;

  const shouldAlert =
    status !== cardExpiryStatus.unknown && (isExpired || isExpiringSoon);

  return {
    ...cardStatus,
    isExpiringSoon,
    shouldAlert,
  };
};
